import { RfqQuotationHistory, RfqHistoricalQuote, type Item, type HistoricalQuote } from "./rfq/RfqQuotationHistory";
import { DetailBreadcrumb } from "../components/DetailBreadcrumb";
import { ClosedPostNotice } from "../components/ClosedPostNotice";
import { RfqRequesterCard } from "../components/RfqRequesterCard";
import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { useRoute, Link, useLocation, useSearch } from "wouter";
import { MobileActionBar } from "../components/MobileActionBar";
import { Clock, Package, ReceiptText, MessageSquare, Tag } from "lucide-react";
import { LocationPin } from "../components/LocationPin";
import { DetailLoading, DetailNotFound } from "../components/DetailStates";
import { useListing } from "../lib/useListing";
import { rfqs as mockRfqs, peso } from "../data/mock";
import { api, type ServiceQuoteInput, type RfqItemQuoteInput, type RfqItemQuote, type RfqItemProgress } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SuggestedOrgs } from "../components/SuggestedOrgs";
import { useInPageActionVisibility } from "../lib/useInPageActionVisibility";
import { RfqDetailMobile } from "../components/RfqDetailMobile";
import { ServiceQuotationForm } from "../components/ServiceQuotation";
import { useI18n } from "../lib/i18n";
import { ImageCarousel } from "../components/ImageCarousel";
import { RfqTypeBadge } from "../components/RfqTypeBadge";
import { RfqItemProgressPanel, RfqItemQuotationForm } from "../components/RfqItemQuotation";
import { RfqDesiredTermsSummary } from "../components/RfqDesiredTerms";
import { rfqDeadlineExpired } from "../lib/rfqDeadline";


export default function RfqDetail() {
  const { t } = useI18n();
  const [, params] = useRoute("/rfq/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";
  const search = useSearch();
  const [progress, setProgress] = useState<RfqItemProgress[] | null>(null);
  const [itemEligible, setItemEligible] = useState<boolean | null>(null);
  const [itemQuotes, setItemQuotes] = useState<RfqItemQuote[]>([]);
  const { item: rfq, state } = useListing("rfqs", id, mockRfqs);
  const [qData, setQData] = useState<Awaited<ReturnType<typeof api.getQuotes>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showQuotation, setShowQuotation] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const [isMobileViewport, setIsMobileViewport] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches);
  // Mobile watches the main CTA after loading; desktop retains its sidebar target.
  const actionVisibilityKey = isMobileViewport
    ? state === "ready" ? `${id}:mobile:${user?.id ?? "guest"}:${qData?.myQuote?.threadId ?? ""}:${itemQuotes.length}` : null
    : id;
  const { actionRef: quoteRef, isVisible } = useInPageActionVisibility<HTMLDivElement>(actionVisibilityKey);
  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobileViewport(viewport.matches);
    viewport.addEventListener("change", update);
    return () => viewport.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    setQData(null); setErr(null); setShowQuotation(false);
    if (!user) return;
    let active = true;
    api.getQuotes(id).then((value) => { if (active) setQData(value); }).catch(() => { if (active) setErr("Could not load quotation history."); });
    return () => { active = false; };
  }, [id, user?.id]);
  const items: Item[] = useMemo(() => Array.isArray(rfq?.items) ? rfq.items : [], [rfq]);
  const detail = rfq?.detail && typeof rfq.detail === "object" ? rfq.detail : {};
  // [rfq.request-type](../../../docs/product/rfqs.md#rfq-request-type): old Services lines remain visible; new Services requests use Description.
  const showLineItems = detail.requestType !== "services" || items.length > 0;
  const isItems = detail.requestType === "items";
  useEffect(() => {
    setProgress(null); setItemQuotes([]); setItemEligible(null);
    if (!isItems) return;
    let active = true;
    api.getRfqItemProgress(id).then(value => { if (active) setProgress(value.items); }).catch(() => { if (active) setErr("Could not load item availability. Please refresh before quoting."); });
    if (user) api.getRfqItemQuotes(id).then(value => { if (active) { setItemQuotes(value.quotes); setItemEligible(value.canQuote ?? user.workerVerified); } }).catch(() => { if (active) setErr("Could not load Items quotations."); });
    return () => { active = false; };
  }, [id, isItems, user?.id, user?.workerVerified]);
  const standby = !isItems && detail.requestType !== "services";
  const canQuote = !!user && (isItems ? (itemEligible ?? user.workerVerified) : ["offer_service", "promote_company"].some((capability) => user.capabilities?.includes(capability)));
  if (state === "loading") return <DetailLoading label="Loading RFQ…" />;
  if (state === "notfound" || !rfq) return <DetailNotFound title="RFQ not found" backHref="/marketplace?tab=RFQs" backLabel="Browse RFQs" />;
  const isOwner = !!user && rfq.ownerUserId === user.id;
  const myQuote = qData?.myQuote || null;
  const awarded = rfq.status === "awarded" || qData?.status === "awarded";
  const deadlineExpired = !awarded && rfq.status === "open" && rfqDeadlineExpired(rfq.deadline, now);
  const closed = deadlineExpired || awarded || rfq.status !== "open" || (isItems && !!progress?.length && progress.every(item => item.available === 0));
  const ownItemQuote = itemQuotes.find(quote => quote.sellerUserId === user?.id);
  const reviseId = new URLSearchParams(search).get("revise");
  const revision = itemQuotes.find(quote => quote.id === reviseId && quote.sellerUserId === user?.id && ["sent", "changes_requested"].includes(quote.status));
  const progressPanel = isItems && progress && <RfqItemProgressPanel items={progress} />;
  async function sendItemsQuotation(quote: RfqItemQuoteInput) {
    setBusy(true); setErr(null);
    try { const sent = await api.sendRfqItemQuote(id, quote); setShowQuotation(false); navigate(`/messages/${encodeURIComponent(sent.threadId)}`); }
    finally { setBusy(false); }
  }

  async function sendQuotation(quote: ServiceQuoteInput) {
    setBusy(true); setErr(null);
    try {
      const sent = await api.sendRfqServiceQuote(id, quote);
      setShowQuotation(false);
      navigate(`/messages/${encodeURIComponent(sent.threadId)}`);
    } finally { setBusy(false); }
  }
  const quotationForm = showQuotation && (isItems && progress ? <RfqItemQuotationForm key={revision?.id || id} title={rfq.title} items={progress} initialQuote={revision} busy={busy} onCancel={() => { if (!busy) setShowQuotation(false); }} onSend={sendItemsQuotation} onPreview={input => api.previewRfqItemQuote(id, input)} /> : <ServiceQuotationForm key="rfq-quotation" rfq serviceTitle={rfq.title} onCancel={() => { if (!busy) setShowQuotation(false); }} onPreview={async quote => (await api.previewRfqServiceQuote(id, quote)).document} onSend={sendQuotation} busy={busy} />);
  const historicalQuotePanel = (quote: HistoricalQuote) => <RfqHistoricalQuote quote={quote} items={items} />;
  const quotesPanel = isOwner && <RfqQuotationHistory key={`${id}:${isItems}:${user?.id}:${user?.workerVerified}`} isItems={isItems} standby={standby} err={err} items={items} itemQuotes={itemQuotes} qData={qData} />;
  const quotePanel = !isOwner && <div ref={quoteRef} className="card min-w-0 p-4 sm:p-5">
    <h3 className="font-bold text-navy">{standby ? "Quotation submission coming soon" : isItems ? "Items quotation" : "Service quotation"}</h3>
    {err && <p className="mt-2 text-xs text-brand-red">{err}</p>}
    {standby ? <p className="mt-3 text-sm text-mut">{detail.requestType === "items" ? "Item RFQ quotations are on standby while we prepare this flow." : "This RFQ has no specified type. Quotation submission is on standby."}</p>
      : closed && !revision ? <p className="mt-3 text-sm text-mut">{deadlineExpired ? "Quotation deadline passed." : "This RFQ is closed for new quotations."}</p>
      : !user ? <button onClick={() => navigate("/login")} className="btn-red mt-3 w-full">Sign in to quote</button>
      : !canQuote ? <><p className="mt-3 text-sm text-mut">{isItems ? "Complete identity verification to send Items quotations. A catalog listing is optional." : "Enable Offer a Service or Promote a Company to send quotations."}</p><button onClick={() => navigate("/dashboard")} className="btn-outline mt-3 w-full">{isItems ? "Complete verification" : "Manage activities"}</button></>
      : <><p className="mt-3 text-sm text-mut">{isItems ? "Select the items and whole quantities you can supply, then enter unit prices and supply terms." : "Send your scope, price, exclusions, delivery time, and payment milestones to the buyer in Messages."}</p>
        <button disabled={isItems && !progress} onClick={() => setShowQuotation(true)} className="btn-red mt-3 w-full disabled:opacity-50">{revision ? "Revise quotation" : "Create quotation"}</button></>}
    {ownItemQuote && <Link href={`/messages/${encodeURIComponent(ownItemQuote.threadId)}`} className="btn-outline mt-3 inline-flex">View Items quotation in Messages</Link>}
    {myQuote?.threadId && <Link href={`/messages/${encodeURIComponent(myQuote.threadId)}`} className="btn-outline mt-3 inline-flex h-9 px-3 text-xs">View quotation in Messages</Link>}
    {myQuote?.historicalQuote && historicalQuotePanel(myQuote.historicalQuote)}
    {myQuote && !myQuote.threadId && <div className="mt-3 border-t border-line pt-3"><p className="text-xs text-mut">Historical sealed quote · {peso(myQuote.total)}</p></div>}
  </div>;
  const suggestedOrgs = <SuggestedOrgs kind="rfqs" id={id} />;
  const requesterCard = <RfqRequesterCard postingIdentity={rfq.postingIdentity} summary={rfq.requesterSummary} ownerUserId={rfq.ownerUserId} />;
  const quotationThread = ownItemQuote?.threadId || myQuote?.threadId;
  const quotationAction = {
    available: !standby && (!closed || !!revision || !!quotationThread),
    disabled: busy || (isItems && !progress && !!user && canQuote && !quotationThread),
    label: !user ? "Sign in to quote" : revision ? "Revise quotation" : quotationThread ? "View your quote" : !canQuote ? (isItems ? "Complete verification" : "Manage activities") : "Create Quotation",
    onClick: () => {
      if (!user) { navigate("/login"); return; }
      if (quotationThread && !revision) { navigate(`/messages/${encodeURIComponent(quotationThread)}`); return; }
      if (!canQuote) { navigate("/dashboard"); return; }
      if (!isOwner && !standby && (!closed || revision) && (!isItems || progress)) setShowQuotation(true);
    },
  };
  const mobileDetailProps: ComponentProps<typeof RfqDetailMobile> = {
    rfq, items, signedIn: !!user, canQuote, isOwner, awarded, closed, deadlineExpired, myQuote, quoteError: err,
    quotePanel, quotesPanel, suggestedOrgs, quoteRef, quoteIsVisible: isVisible, standby,
    quotationAction,
    historicalQuoteContent: !isOwner && myQuote && (myQuote.historicalQuote ? historicalQuotePanel(myQuote.historicalQuote) : !myQuote.threadId ? historicalQuotePanel(myQuote) : null),
    itemProgressPanel: progressPanel, requesterCard, onSignIn: () => navigate("/login"),
    onManageActivities: () => navigate("/dashboard"),
    onMessageBuyer: () => navigate(`/messages?to=${rfq.ownerUserId}&type=rfq&id=${encodeURIComponent(id)}&name=${encodeURIComponent(rfq.owner)}`),
  };
  const mobileDetail = <RfqDetailMobile {...mobileDetailProps} />;

  return <>
    <div className="container-x pt-4"><ClosedPostNotice post={rfq || {}} /></div>
    {isMobileViewport ? mobileDetail : (
    <div className={`container-x min-w-0 py-6 ${!isVisible ? "pb-32 lg:pb-6" : ""}`}>
      <DetailBreadcrumb title={rfq.title} fallback={{ href: "/marketplace?tab=RFQs", label: "RFQs" }} className="mb-4 text-sm" />

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="min-w-0 space-y-4">
          <ImageCarousel type="rfq" id={id} />
          <div className="card p-4 sm:p-6">
            <div className="mb-2 flex flex-wrap items-center gap-2 [&>span]:shrink-0 [&>span]:whitespace-nowrap">
              {awarded
                ? <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>
                : <span className="chip bg-brand-green/10 text-brand-greendark">{rfq.status === "completed" ? "Completed" : deadlineExpired ? "Quotation deadline passed" : closed ? "Closed" : t("detail.rfq.openQuotes", "Open for Quotes")}</span>}
              <RfqTypeBadge requestType={detail.requestType} />
            </div>
            <h1 className="break-words text-2xl font-extrabold text-navy">{rfq.title}</h1>
            <div className="mt-2 grid min-w-0 grid-cols-1 gap-2 text-sm text-mut">
              <span className="flex min-w-0 items-start gap-1"><LocationPin className="mt-0.5 h-4 w-4 shrink-0" /><span className="min-w-0 break-words">{t("posting.location", "Location")}: {rfq.location}</span></span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4 shrink-0" />{t("detail.rfq.due")}: {rfq.deadline}</span>
              <span className="flex min-w-0 items-start gap-1"><Tag aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" /><span className="min-w-0 break-words">{t("detail.rfq.category")}: {rfq.category}</span></span>
            </div>
            {detail.description && <p className="text-sm text-mut mt-3">{detail.description}</p>}
            <RfqDesiredTermsSummary requestType={detail.requestType} milestones={detail.desiredMilestones} items={items} />
            {detail.previousWork && (
              <div className="mt-3 rounded-lg bg-navy-50 p-3">
                <div className="text-xs font-bold text-navy mb-1">{t("posting.previousWork")}</div>
                <p className="text-sm text-mut whitespace-pre-line">{detail.previousWork}</p>
              </div>
            )}
            {user && rfq.ownerUserId != null && rfq.ownerUserId !== user.id && (
              <button onClick={() => navigate(`/messages?to=${rfq.ownerUserId}&type=rfq&id=${encodeURIComponent(id)}&name=${encodeURIComponent(rfq.owner)}`)} className="btn-outline mt-3 h-9 w-full px-3 text-xs sm:w-auto"><MessageSquare className="w-4 h-4" />{t("detail.rfq.messageBuyer")}</button>
            )}
          </div>

          {/* Line items */}
          {(showLineItems || detail.terms) && <div className="card overflow-hidden p-4 sm:p-6">
            {showLineItems && <>
            <h3 className="font-bold text-navy mb-3 flex items-center gap-2"><Package className="w-4 h-4" />{t("detail.rfq.requestedItems")} ({items.length})</h3>
            <div className="min-w-0 overflow-x-auto">
              <table className="min-w-[34rem] w-full text-sm">
                <thead>
                  <tr className="text-left text-mut border-b border-line">
                    <th className="py-2 pr-3 font-semibold">#</th>
                    <th className="py-2 px-3 font-semibold">{t("detail.rfq.item")}</th>
                    <th className="py-2 px-3 font-semibold text-right">{t("detail.rfq.quantity")}</th>
                    <th className="py-2 pl-3 font-semibold">{t("detail.rfq.unit")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-b border-line/60">
                      <td className="py-2 pr-3 text-mut align-top">{i + 1}</td>
                      <td className="py-2 px-3 font-medium text-navy align-top">
                        {it.name}
                        {it.kind === "lump" && <span className="chip bg-navy-50 text-navy-600 ml-2 text-[10px]">Lump sum</span>}
                        {it.spec && <div className="text-xs text-mut font-normal mt-0.5">{it.spec}</div>}
                      </td>
                      <td className="py-2 px-3 text-right text-navy align-top">{it.kind === "lump" ? "—" : it.qty}</td>
                      <td className="py-2 pl-3 text-mut align-top">{it.kind === "lump" ? "—" : (it.unit || "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>}
            {detail.terms && <p className="text-xs text-mut mt-3">{t("detail.rfq.terms")}: {detail.terms}</p>}
          </div>}

          {progressPanel}
          {/* Owner: quote comparison */}
          {quotesPanel}
        </div>

        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-24 self-start h-fit min-w-0 space-y-4 desktop-sidebar-scroll">
          {requesterCard}
          <div className="card p-4 sm:p-5">
            <h3 className="font-bold text-navy mb-3">{t("detail.rfq.summary")}</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-2"><dt className="text-mut">{t("detail.rfq.status")}</dt><dd className="text-right font-semibold text-navy">{awarded ? t("detail.rfq.awarded", "Awarded") : rfq.status === "completed" ? "Completed" : deadlineExpired ? "Quotation deadline passed" : closed ? "Closed" : t("detail.rfq.open", "Open")}</dd></div>
              {showLineItems && <div className="flex justify-between"><dt className="text-mut">{t("detail.rfq.lineItems")}</dt><dd className="font-semibold text-navy">{items.length}</dd></div>}
              <div className="flex justify-between"><dt className="text-mut">{t("detail.rfq.quotesReceived")}</dt><dd className="font-semibold text-navy">{rfq.quotes ?? 0}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">{t("detail.rfq.closes")}</dt><dd className="font-semibold text-navy">{rfq.deadline}</dd></div>
            </dl>
          </div>

          <SuggestedOrgs kind="rfqs" id={id} />

          {quotePanel}
        </aside>
      </div>

      {!isOwner && !closed && !standby && !isVisible && (
        <MobileActionBar>
          <div className="shrink-0">
            <div className="text-[11px] text-mut leading-none">RFQ</div>
            <div className="text-sm font-extrabold text-navy leading-tight">{showLineItems ? `${items.length} item${items.length === 1 ? "" : "s"}` : "Service quotation"}</div>
          </div>
          <button type="button" onClick={() => quoteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="btn-red min-w-0 flex-1 px-3 text-xs sm:text-sm"><ReceiptText className="h-4 w-4 shrink-0" />Create quotation</button>
        </MobileActionBar>
      )}

    </div>
    )}
    {quotationForm}
  </>;
}
